import { useEffect, useMemo, useRef, useState } from "react";
import { ConfidenceMeter } from "@/components/ConfidenceMeter";
import { Boxes, Play, Pause, RotateCcw } from "lucide-react";

const ROLE = {
  victim: "#FF3B30", suspect: "#FFB020", witness: "#007AFF", evidence: "#34D399", object: "#9CA3AF",
};

function pos(kf, id) {
  const p = (kf?.positions || []).find((x) => x.id === id);
  return p || { x: 50, y: 50 };
}

export function SceneReconstruction({ caseData }) {
  const scene = caseData?.case_engines?.scene_reconstruction;
  const keyframes = scene?.keyframes || [];
  const entities = scene?.entities || [];
  const fixtures = scene?.fixtures || [];

  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [tilt, setTilt] = useState(false);
  const timer = useRef(null);

  useEffect(() => { setIdx(0); }, [scene]);
  useEffect(() => {
    if (playing && keyframes.length) {
      timer.current = setInterval(() => {
        setIdx((i) => { if (i >= keyframes.length - 1) { setPlaying(false); return i; } return i + 1; });
      }, 1600);
    }
    return () => clearInterval(timer.current);
  }, [playing, keyframes.length]);

  const trails = useMemo(() => entities.map((e) => ({
    id: e.id, role: e.role,
    pts: keyframes.slice(0, idx + 1).map((kf) => pos(kf, e.id)),
  })), [entities, keyframes, idx]);

  const kf = keyframes[idx];

  return (
    <div className="bg-monk-panel border border-white/10 rounded-md" data-testid="scene-reconstruction">
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
        <Boxes className="w-4 h-4 text-monk-blue" />
        <div className="leading-none flex-1"><div className="font-heading font-700 text-sm tracking-tight">Forensic Scene Reconstruction</div><div className="overline mt-1">§10 · Interactive Walkthrough · Time-Slider</div></div>
        {scene && <div className="w-24 hidden sm:block"><ConfidenceMeter value={scene.confidence || 0} /></div>}
        {!scene && <span className="text-[9px] font-mono text-neutral-600" data-testid="pending-scene_reconstruction">PENDING</span>}
      </div>

      {!scene ? (
        <div className="p-8 text-center">
          <Boxes className="w-8 h-8 text-neutral-700 mx-auto mb-3" />
          <p className="hunch-data text-[13px]">Awaiting auto-analysis — Monk reconstructs the scene automatically as an interactive, time-sliced walkthrough once the case is analyzed.</p>
        </div>
      ) : (
        <div className="p-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Scene board */}
            <div className="lg:col-span-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[12px] text-neutral-300"><span className="font-mono text-monk-blue">{scene.scene_label}</span> <span className="hunch-data">· {scene.dimensions_note}</span></div>
                <button onClick={() => setTilt(!tilt)} data-testid="scene-tilt" className={`text-[10px] font-mono px-2 py-1 rounded-sm border transition-colors ${tilt ? "border-monk-blue/50 text-monk-blue" : "border-white/15 text-neutral-400"}`}>3D {tilt ? "ON" : "OFF"}</button>
              </div>
              <div className="relative rounded-md overflow-hidden border border-white/10 bg-black" style={{ aspectRatio: "16/10", perspective: "1200px" }}>
                <div className="absolute inset-0 transition-transform duration-500" style={{
                  transform: tilt ? "rotateX(52deg) scale(0.86) translateY(-6%)" : "none",
                  transformStyle: "preserve-3d",
                  backgroundImage: "linear-gradient(rgba(0,122,255,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(0,122,255,0.12) 1px, transparent 1px)",
                  backgroundSize: "8% 8%",
                }}>
                  {/* trails */}
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    {trails.map((t) => t.pts.length > 1 && (
                      <polyline key={t.id} points={t.pts.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" stroke={ROLE[t.role] || "#9CA3AF"} strokeWidth="0.5" strokeOpacity="0.5" strokeDasharray="1.5 1.5" />
                    ))}
                  </svg>
                  {/* fixtures */}
                  {fixtures.map((f, i) => (
                    <div key={i} className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" style={{ left: `${f.x}%`, top: `${f.y}%` }}>
                      <div className="w-2 h-2 border border-white/30 bg-white/5" />
                      <span className="text-[7px] font-mono text-neutral-500 mt-0.5 whitespace-nowrap">{f.label}</span>
                    </div>
                  ))}
                  {/* entities */}
                  {entities.map((e) => {
                    const p = pos(kf, e.id);
                    return (
                      <div key={e.id} className="absolute -translate-x-1/2 -translate-y-1/2 transition-all duration-[1400ms] ease-in-out" style={{ left: `${p.x}%`, top: `${p.y}%` }} data-testid={`scene-entity-${e.id}`}>
                        <div className="w-3.5 h-3.5 rounded-full ring-2 ring-black shadow-lg" style={{ background: ROLE[e.role] || "#9CA3AF", boxShadow: `0 0 12px ${ROLE[e.role] || "#9CA3AF"}` }} />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* time slider */}
              <div className="flex items-center gap-3 mt-3">
                <button onClick={() => setPlaying(!playing)} data-testid="scene-play" className="w-9 h-9 rounded-md bg-monk-blue text-white flex items-center justify-center hover:bg-monk-blue/90 transition-colors">
                  {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </button>
                <button onClick={() => { setIdx(0); setPlaying(false); }} className="w-9 h-9 rounded-md border border-white/15 text-neutral-400 flex items-center justify-center hover:text-white transition-colors"><RotateCcw className="w-4 h-4" /></button>
                <input type="range" min={0} max={Math.max(0, keyframes.length - 1)} value={idx} onChange={(e) => { setPlaying(false); setIdx(+e.target.value); }} data-testid="scene-slider"
                  className="flex-1 accent-monk-blue" />
                <span className="font-mono text-[12px] text-monk-blue w-16 text-right">{kf?.t || "—"}</span>
              </div>
              <div className="mt-2 text-[13px] text-neutral-200 min-h-[40px]" data-testid="scene-narration">
                <span className="overline mr-2">T{idx + 1}/{keyframes.length}</span>{kf?.narration}
              </div>
            </div>

            {/* legend + summary */}
            <div className="space-y-3">
              <div className="overline">Actors</div>
              <div className="space-y-1.5">
                {entities.map((e) => (
                  <div key={e.id} className="flex items-center gap-2 text-[12px]">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: ROLE[e.role] || "#9CA3AF" }} />
                    <span className="text-neutral-200 truncate">{e.label}</span>
                    <span className="overline ml-auto">{e.role}</span>
                  </div>
                ))}
              </div>
              <div className="pt-2 border-t border-white/10">
                <div className="overline mb-1">Reconstruction</div>
                <p className="hard-data text-[12px] text-neutral-300 leading-relaxed">{scene.reconstruction_summary}</p>
              </div>
              {scene.monk_note && <div className="flex items-start gap-2 border-l-2 border-monk-blue/50 pl-2"><span className="overline text-monk-blue shrink-0 mt-0.5">Monk</span><p className="hunch-data text-[12px]">{scene.monk_note}</p></div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
